import axios from 'axios'

import { API_BASE_URL } from './client'
import { ApiError } from './errors'
import type { PublicUploadResult, UploadLinkInfo } from '@/types/domain'

/**
 * The public upload-link endpoints (`/public/upload/:token`, not under
 * `/v1`), used by the capture build, which runs with no session at all.
 *
 * Unlike every other module here this one builds its own axios instance:
 * it must never attach an Authorization header, never trigger a token
 * refresh, and never send credentials.
 */
const http = axios.create({ baseURL: API_BASE_URL, withCredentials: false })

export const publicUploadApi = {
  /** A token's public metadata. 404 for an unknown, revoked or expired one. */
  async info(token: string): Promise<UploadLinkInfo> {
    try {
      const { data } = await http.get<UploadLinkInfo>(`/public/upload/${token}`)
      return data
    } catch (error) {
      throw ApiError.from(error)
    }
  },

  async upload(
    token: string,
    file: File | Blob,
    filename: string,
    onProgress?: (sent: number, total: number) => void,
  ): Promise<PublicUploadResult> {
    const form = new FormData()
    form.append('file', file, filename)
    try {
      const { data } = await http.post<PublicUploadResult>(`/public/upload/${token}`, form, {
        onUploadProgress: (event) => {
          if (onProgress) onProgress(event.loaded, event.total ?? 0)
        },
      })
      return data
    } catch (error) {
      throw ApiError.from(error)
    }
  },
}
